import { Alert } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { Button } from "../../common/Button";
import { useSchedulingOwner } from "../../../hooks/ReactQueryHooks/useSchedulingOwner";
import { useSchedulingPermissions } from "./permissions";
import { useSchedulesQuery } from "../../../hooks/ReactQueryHooks/useSchedulesQuery";
import { useAvailabilityQuery } from "../../../hooks/ReactQueryHooks/useAvailabilityQuery";
import { useCalendarBlocksQuery } from "../../../hooks/ReactQueryHooks/useCalendarBlocksQuery";
import { useAppointmentsQuery } from "../../../hooks/ReactQueryHooks/useAppointmentsQuery";
import { useServiceCategoriesQuery } from "../../../hooks/ReactQueryHooks/useServiceCategoriesQuery";
import { displayPractitionerName, practitionerIdFromUser, loadPatientName } from "./names";
import { subjectPatientRef } from "./appointments/statusLabels";
import AppointmentMoveModal from "./calendar/AppointmentMoveModal";
import { canMoveOwnAppointment, getMyScheduleMove } from "./calendar/myScheduleMove";
import { instantRangeToLocal } from "./time";
import { useSchedulingTimeZone } from "../../../hooks/useSchedulingTimeZone";
import TimeZoneSelect from "./TimeZoneSelect";
import { buildCalendarEvents } from "./calendar/eventModel";
import SchedulingCalendar from "./calendar/SchedulingCalendar";
import CalendarFilters from "./calendar/CalendarFilters";
import ScheduleSelectionModal from "./ScheduleSelectionModal";
import ManualAvailabilityForm from "./ManualAvailabilityForm";
import CalendarBlockForm from "./CalendarBlockForm";
import CalendarBlockDetailModal from "./CalendarBlockDetailModal";
import WorkingHoursDetailModal from "./WorkingHoursDetailModal";
import AppointmentDetailModal from "./appointments/AppointmentDetailModal";
import "./Scheduling.css";

const ScheduleCalendar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { excludedOrganizationRefs, organizationRef, practitionerRef, isSuperAdmin, organizationTimeZone, user } = useSchedulingOwner();
  const displayZone = useSchedulingTimeZone(user);
  const timezoneName = displayZone.timeZone;
  const { canCreateEditSchedule, canOpenSchedule, canManageAppointments } = useSchedulingPermissions();
  const selectedPractitioner = location.state?.data;
  const targetPractitionerRef = selectedPractitioner
    ? practitionerIdFromUser(selectedPractitioner)
    : practitionerRef;
  const [eventFilter, setEventFilter] = useState("all");
  const [slotMinutes, setSlotMinutes] = useState(20);
  const [range, setRange] = useState({ startsAt: null, endsAt: null });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [createChoice, setCreateChoice] = useState(null);
  const [windowModal, setWindowModal] = useState(null);
  const [blockModal, setBlockModal] = useState(null);
  const [appointmentModal, setAppointmentModal] = useState(null);
  const [appointmentMove, setAppointmentMove] = useState(null);
  useEffect(() => { setAppointmentMove(null); }, [organizationRef, targetPractitionerRef, timezoneName]);
  const requestAppointmentMove = move => {
    setSelectedEvent(null);
    setAppointmentModal(null);
    setCreateChoice(null);
    setAppointmentMove({ ...move, patientName: "Loading patient…" });
    const ref = subjectPatientRef(move.appointment);
    loadPatientName(ref).then(name => setAppointmentMove(current => current?.appointment === move.appointment && current.startsAt === move.startsAt
      ? { ...current, patientName: name && name !== ref && name !== "Unknown" ? name : "Patient name unavailable" } : current));
  };

  const { data: schedules = [], isLoading: schedulesLoading, error: schedulesError } = useSchedulesQuery({
    organizationRef,
    practitionerRef: targetPractitionerRef,
    status: "active",
    enabled: !!targetPractitionerRef,
  });
  const schedule = schedules[0];
  const { data: categories = [] } = useServiceCategoriesQuery({ scope: "descendants", excludedOrganizationRefs, organizationRef, active: undefined });
  const { data: availability = [], refetch: refetchAvailability, isLoading: availabilityLoading, error: availabilityError } = useAvailabilityQuery({
    organizationRef,
    scheduleId: schedule?.scheduleId,
    startsAt: range.startsAt,
    endsAt: range.endsAt,
    enabled: !!schedule && !!range.startsAt,
  });
  const { data: blocks = [], refetch: refetchBlocks, isLoading: blocksLoading, error: blocksError } = useCalendarBlocksQuery({
    organizationRef,
    scheduleId: schedule?.scheduleId,
    startsAt: range.startsAt,
    endsAt: range.endsAt,
    enabled: !!schedule && !!range.startsAt,
  });
  const { data: appointments = [], refetch: refetchAppointments, isLoading: appointmentsLoading, error: appointmentsError } = useAppointmentsQuery({
    scope: "descendants", excludedOrganizationRefs, organizationRef,
    practitionerRef: targetPractitionerRef,
    startsAt: range.startsAt,
    endsAt: range.endsAt,
    enabled: !!targetPractitionerRef && !!range.startsAt,
  });

  const events = useMemo(
    () =>
      buildCalendarEvents({
        availability,
        blocks,
        appointments,
        categories,
        showBookings: true,
      }),
    [availability, blocks, appointments, categories]
  );

  if (!canOpenSchedule) {
    navigate("/app/dashboard");
    return null;
  }

  if (isSuperAdmin && !selectedPractitioner && !practitionerRef) {
    navigate("/app/providers-schedule-appointments");
    return null;
  }

  if (selectedPractitioner && !targetPractitionerRef) {
    return (
      <section className="common-listing scheduling-workspace schedule-calendar-page">

        <p>This user has no practitioner record.</p>
      </section>
    );
  }

  const calendarLoading = availabilityLoading || blocksLoading || appointmentsLoading;
  const calendarError = availabilityError || blocksError || appointmentsError;
  const title = selectedPractitioner
    ? `Schedule of ${displayPractitionerName(selectedPractitioner)}`
    : "Manage Schedule";

  return (
    <section className="common-listing scheduling-workspace schedule-calendar-page">
      <div className="schedule-calendar-page-heading">
        <h1>
          {title}
        </h1>
        <div className="right-wrap" style={{ display: "flex", gap: 8 }}>
          {canCreateEditSchedule && !schedule ? (
            <Button
              onClick={() =>
                navigate("/app/create-schedule", { state: { data: selectedPractitioner, organizationRef } })
              }
            >
              Create schedule
            </Button>
          ) : null}
          {canCreateEditSchedule && schedule ? (
            <Button
              onClick={() =>
                navigate(`/app/edit-schedule?scheduleId=${encodeURIComponent(schedule.scheduleId)}&organizationRef=${encodeURIComponent(organizationRef)}`, {
                  state: { scheduleId: schedule.scheduleId, data: selectedPractitioner, organizationRef },
                })
              }
            >
              Edit schedule & hours
            </Button>
          ) : null}
        </div>
      </div>
      {schedule && <p className="schedule-page-description">Your working hours, appointments and time set aside — in one place.</p>}
      {calendarLoading && <p role="status">Loading schedule…</p>}
      {calendarError && <Alert severity="error" action={<button onClick={() => { refetchAvailability(); refetchBlocks(); refetchAppointments(); }}>Retry</button>}>{calendarError.message || "Could not load the complete schedule."}</Alert>}
      {schedulesLoading ? <p role="status">Loading schedule…</p> : schedulesError ? <Alert severity="error">Could not load work schedules.</Alert> : !schedule ? (
        <p>No work schedule yet. Create one to choose appointment types and set your working hours.</p>
      ) : (
        <SchedulingCalendar
          controls={<CalendarFilters filter={eventFilter} onFilterChange={setEventFilter} slotMinutes={slotMinutes} onSlotMinutesChange={setSlotMinutes} />}
          events={(calendarLoading || calendarError ? [] : events).filter((event) => eventFilter === "all" || event.extendedProps.kind === eventFilter)}
          slotMinutes={slotMinutes}
          canDragAppointment={event => canManageAppointments && !calendarLoading && !calendarError && canMoveOwnAppointment(event, targetPractitionerRef, schedule.scheduleId)}
          resolveAppointmentMove={(event, requestedStart) => getMyScheduleMove({ event, requestedStart, practitionerRef: targetPractitionerRef, scheduleId: schedule.scheduleId, categories, availability, blocks })}
          onAppointmentMove={canManageAppointments ? requestAppointmentMove : undefined}
          timeZone={timezoneName}
          timeZoneControl={<TimeZoneSelect value={displayZone.preference} onChange={displayZone.setPreference}
            localZone={displayZone.deviceTimeZone} scheduleZone={schedule.timezoneName} organizationZone={organizationTimeZone} />}
          selectable={!calendarLoading && !calendarError && canCreateEditSchedule && schedule.status === "active"}
          selectedEventId={
            selectedEvent?.kind === "availability"
              ? `availability-${selectedEvent.source?.availabilityId}`
              : selectedEvent?.kind === "block"
                ? `block-${selectedEvent.source?.blockId}`
                : selectedEvent?.kind === "appointment"
                  ? `appointment-${selectedEvent.source?.appointmentId}`
                  : undefined
          }
          onDatesSet={({ start, end }) =>
            setRange({ startsAt: start.toISOString(), endsAt: end.toISOString() })
          }
          onEventClick={(event, props) => {
            setCreateChoice(null);
            setSelectedEvent(props);
            if (props.kind === "appointment") setAppointmentModal(props.source);
          }}
          onSelect={(info) => {
            if (!canCreateEditSchedule) return;
            setSelectedEvent(null);
            setCreateChoice({ ...instantRangeToLocal(info.startStr, info.endStr, timezoneName), timezoneName });
          }}
        />
      )}

      {selectedEvent?.kind === "availability" ? (
        <WorkingHoursDetailModal
          window={selectedEvent.source}
          categories={categories}
          timezoneName={timezoneName}
          canEdit={canCreateEditSchedule}
          onHide={() => setSelectedEvent(null)}
          onEdit={(source) => { setWindowModal({ ...source, timezoneName }); setSelectedEvent(null); }}
          onBlock={(source) => {
            const end = moment.min(moment(source.startsAt).add(slotMinutes, "minutes"), moment(source.endsAt));
            setBlockModal({
              ...instantRangeToLocal(source.startsAt, end.toISOString(), timezoneName), timezoneName,
            });
            setSelectedEvent(null);
          }}
        />
      ) : null}

      {selectedEvent?.kind === "block" && (
        <CalendarBlockDetailModal
          key={selectedEvent.source.blockId}
          block={selectedEvent.source}
          organizationRef={organizationRef}
          timezoneName={timezoneName}
          canEdit={canCreateEditSchedule}
          onHide={() => setSelectedEvent(null)}
          onEdit={(block) => { setBlockModal({ ...block, timezoneName }); setSelectedEvent(null); }}
          onRemoved={() => { setSelectedEvent(null); refetchBlocks(); }}
          onOpenAppointment={canManageAppointments ? appointment => { setSelectedEvent(null); setAppointmentModal(appointment); } : undefined}
          appointmentScope="descendants"
          excludedOrganizationRefs={excludedOrganizationRefs}
        />
      )}

      {createChoice && (
        <ScheduleSelectionModal
          selection={createChoice}
          timezoneName={createChoice.timezoneName}
          onHide={() => setCreateChoice(null)}
          onAddHours={(selection) => {
            setWindowModal(selection);
            setCreateChoice(null);
          }}
          onBlockTime={(selection) => {
            setBlockModal(selection);
            setCreateChoice(null);
          }}
        />
      )}

      {windowModal ? (
        <ManualAvailabilityForm
          show
          onHide={() => setWindowModal(null)}
          organizationRef={organizationRef}
          schedule={schedule}
          timezoneName={windowModal.timezoneName}
          window={windowModal.availabilityId ? windowModal : null}
          initialRange={windowModal.availabilityId ? null : windowModal}
          onSaved={() => {
            refetchAvailability();
            refetchBlocks();
            setWindowModal(null);
          }}
        />
      ) : null}
      {blockModal ? (
        <CalendarBlockForm
          show
          onHide={() => setBlockModal(null)}
          organizationRef={organizationRef}
          schedule={schedule}
          timezoneName={blockModal.timezoneName}
          block={blockModal.blockId ? blockModal : null}
          initialRange={blockModal.blockId ? null : blockModal}
          onSaved={(savedBlock) => {
            refetchBlocks();
            setBlockModal(null);
            if (savedBlock?.blockId) setSelectedEvent({ kind: "block", source: savedBlock });
          }}
        />
      ) : null}
      {appointmentMove && <AppointmentMoveModal move={appointmentMove} organizationRef={organizationRef}
        names={{ [targetPractitionerRef]: displayPractitionerName(selectedPractitioner || user) }} timeZone={timezoneName}
        onHide={() => setAppointmentMove(null)} onChanged={() => { setAppointmentMove(null); refetchAppointments(); refetchBlocks(); }} />}
      {appointmentModal ? (
        <AppointmentDetailModal
          timezoneName={timezoneName}
          scheduleTimezoneName={schedule?.timezoneName}
          appointment={appointmentModal}
          organizationRef={organizationRef}
          onHide={() => {
            setAppointmentModal(null);
            setSelectedEvent(null);
          }}
          onChanged={() => {
            refetchAppointments();
            refetchBlocks();
            setAppointmentModal(null);
            setSelectedEvent(null);
          }}
        />
      ) : null}
    </section>
  );
};

export default ScheduleCalendar;
